import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiParameters } from 'src/app/configs/api.params';
import { IPreset, ITarget } from '../models/preset.model';
import { IUserModel } from '../models/user.model';
import { distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PresetService {
  private token: string;
  private user: any;
  public apiEndPoint = apiParameters.url;
  public allPresets$ = new BehaviorSubject<any>(null);
  public allPresets = this.allPresets$
    .asObservable()
    .pipe(distinctUntilChanged());
  public selectedPresets$ = new BehaviorSubject<any>(null);
  public selectedPresets = this.selectedPresets$
    .asObservable()
    .pipe(distinctUntilChanged());
  //if we have two preview presets on the page
  public allPresets2$ = new BehaviorSubject<any>(null);
  public allPresets2 = this.allPresets2$
    .asObservable()
    .pipe(distinctUntilChanged());
  public selectedPresets2$ = new BehaviorSubject<any>(null);
  public selectedPresets2 = this.selectedPresets2$
    .asObservable()
    .pipe(distinctUntilChanged());

  // public apiParam = '/users/7';

  constructor(private httpClient: HttpClient) {}

  public setAllPresets(preset) {
    this.allPresets$.next(preset);
  }
  public setSelectedPresets(preset) {
    this.selectedPresets$.next(preset);
  }
  public setAllPresets2(preset) {
    this.allPresets2$.next(preset);
  }
  public setSelected2Presets(preset) {
    this.selectedPresets2$.next(preset);
  }

  // Get loged in user
  getLoggedInUser(): IUserModel {
    return JSON.parse(sessionStorage.getItem('currentUser'));
  }

  /**
   * This method gets list of presets
   * @returns List of presets
   */
  getPresets(): Observable<IPreset[]> {
    const body: any = {};
    return this.httpClient.get(apiParameters.url + '/presets').pipe(
      map((presets: IPreset[]) => {
        presets.sort((a, b) => {
          const textA = a.name.toUpperCase();
          const textB = b.name.toUpperCase();
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        });
        return presets;
      })
    );
  }

  /**
   * Gets customer presets
   * @param customerId - customer id
   * @returns Customer presets
   */
  getCustomerPresets(customerId: string): Observable<IPreset[]> {
    return this.httpClient
      .get(apiParameters.url + '/customers/' + customerId + '/presets')
      .pipe(
        map((res: IPreset[]) => {
          return res;
        })
      );
  }

  /**
   * Gets client presets and sort by created date
   * @param clientId - client id
   * @returns Client presets
   */
  getClientPresets(clientId: string): Observable<IPreset[]> {
    return this.httpClient
      .get(apiParameters.url + '/clients/' + clientId + '/presets')
      .pipe(
        map((res: IPreset[]) => {
          res.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  /**
   * Adds customer preset
   * @param customerId - customer id
   * @param presetId - preset id
   * @returns Customer preset
   */
  addCustomerPreset(
    customerId: string,
    presetId: string
  ): Observable<IPreset[]> {
    return this.httpClient
      .post(apiParameters.url + '/customers/' + customerId + '/presets', {
        presetId,
      })
      .pipe(
        map((res: IPreset[]) => {
          return res;
        })
      );
  }

  /**
   * Adds client preset
   * @param clientId - client id
   * @param presetId - preset id
   * @returns Client preset
   */
  addClientPreset(clientId: string, presetId: string): Observable<IPreset[]> {
    return this.httpClient
      .post(apiParameters.url + '/clients/' + clientId + '/presets', {
        presetId,
      })
      .pipe(
        map((res: IPreset[]) => {
          return res;
        })
      );
  }

  /**
   * Deletes custromer preset
   * @param customerId
   * @param presetId
   * @returns Custromer preset
   */
  deleteCustromerPreset(
    customerId: string,
    presetId: string
  ): Observable<IPreset> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };

    return this.httpClient
      .delete(
        apiParameters.url + '/customers/' + customerId + '/presets/' + presetId
      )
      .pipe(
        map((res: IPreset) => {
          return res;
        })
      );
  }

  /**
   * Deletes client preset
   * @param clientId - client id
   * @param presetId - preset id
   * @returns Client preset
   */
  deleteClientPreset(clientId: string, presetId: string): Observable<IPreset> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };

    return this.httpClient
      .delete(
        apiParameters.url + '/clients/' + clientId + '/presets/' + presetId
      )
      .pipe(
        map((res: IPreset) => {
          return res;
        })
      );
  }

  /**
   * Gets targets
   * @returns targets
   */
  getTargets(): Observable<ITarget[]> {
    const body: any = {};
    return this.httpClient.get<ITarget[]>(apiParameters.url + '/targets').pipe(
      map((res) => {
        res.sort((a, b) => {
          return (
            (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
          );
        });
        return res;
      })
    );
  }

  /**
   * Deletes target
   * @param targetId
   * @returns target
   */
  deleteTarget(targetId: string): Observable<any> {
    const body: any = {};
    return this.httpClient.delete<any>(
      apiParameters.url + '/targets/' + targetId
    );
  }

  getPresetsOfUser(id: string, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams,
    };

    const body: any = {};
    return this.httpClient
      .get(apiParameters.url + '/users/' + id + '/presets', httpOptions)
      .pipe(
        map((res: any) => {
          res.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  getPreset(id: string, presetId, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams,
    };

    return this.httpClient
      .get(
        apiParameters.url + '/users/' + id + '/presets/' + presetId,
        httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  assignToUser(userId: string, preset: string, scaleConfiguration?: any): Observable<any> {
    let body: any;

    body = {
      presetId: preset,
    };

    if (scaleConfiguration !== undefined || scaleConfiguration !== 'null') {
      body['scaleConfiguration'] = scaleConfiguration;
    }

    return this.httpClient
      .post(apiParameters.url + '/users/' + userId + '/presets', body)
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  removeAssignedPreset(userId: string, presetId: string) {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    let params = '';

    // if (presets && presets.length) {
    //   presets.forEach(id => {
    //     const oneId = 'presets[]=' + id + '&';
    //     params = params + oneId;
    //   })
    // } else {
    //   console.log("nemaa")
    //   return;
    // }

    if (params !== '') {
      params = params.substring(0, params.length - 1);
    }

    return this.httpClient
      .delete(
        apiParameters.url + '/users/' + userId + '/presets/' + presetId,
        httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  editAssignedPreset(userId: string, presetId: string, body) {
    return this.httpClient
      .patch(
        apiParameters.url + '/users/' + userId + '/presets/' + presetId,
        body
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  // removePresetsOfUser(id:string, presets: string[]): Observable<any> {
  //   const currentUser = this.getLoggedInUser();
  //   const token = currentUser.token;
  //   const userId = currentUser.user.id;
  //   const httpOptions = {
  //     headers: new HttpHeaders({
  //       'Authorization': 'Bearer ' + token
  //     })
  //   };
  //   const body: any = {}
  //   return this.httpClient.delete(apiParameters.url + '/users' + id + '/presets', body ,httpOptions)
  //     .pipe(map((res: any) => {
  //       return res;
  //     })
  //     );
  // }

  addPreset(body: any): Observable<IPreset> {
    return this.httpClient.post(apiParameters.url + '/presets', body).pipe(
      map((res: IPreset) => {
        return res;
      })
    );
  }

  editPreset(presetId: string, body: any): Observable<IPreset> {
    return this.httpClient
      .patch(apiParameters.url + '/presets/' + presetId, body)
      .pipe(
        map((res: IPreset) => {
          return res;
        })
      );
  }

  deletePreset(id: string): Observable<any> {
    return this.httpClient.delete(apiParameters.url + '/presets/' + id).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  copyPreset(presetId: string, body?: any): Observable<IPreset> {
    return this.httpClient
      .post(apiParameters.url + '/presets/' + presetId + '/copy', body)
      .pipe(
        map((res: IPreset) => {
          return res;
        })
      );
  }

  getPresetTargets(id: string): Observable<ITarget[]> {
    return this.httpClient
      .get(apiParameters.url + '/presets/' + id + '/targets')
      .pipe(
        map((res: ITarget[]) => {
          return res;
        })
      );
  }

  getPresetTarget(presetId, targetId: string): Observable<ITarget> {
    return this.httpClient
      .get(apiParameters.url + '/presets/' + presetId + '/targets/' + targetId)
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  getTarget(targetId: string): Observable<ITarget> {
    return this.httpClient.get(apiParameters.url + '/targets/' + targetId).pipe(
      map((res: ITarget) => {
        return res;
      })
    );
  }

  addTarget(
    form: NgForm,
    tutorialMessages,
    extraMessages,
    availableFeatures
  ): Observable<ITarget> {
    const {
      position,
      picturePosition,
      showLibela,
      clearType,
      name,
      equipment,
      type,
      tagName
    } = form.form.value;

    let body: any = {};

    body = {
      position,
      picturePosition,
      showLibela,
      clearType,
      name,
      equipment,
      tutorialMessages,
      extraMessages,
      type,
      tagName
    };

    if (availableFeatures.length) {
      body['availableFeatures'] = availableFeatures;
    }

    if (form.form.value.helpMask != null) {
      body.helpMask = form.form.value.helpMask;
    }
    if (form.form.value.cropType !== '') {
      body.cropType = form.form.value.cropType;
    }

    return this.httpClient.post(apiParameters.url + '/targets', body).pipe(
      map((res: ITarget) => {
        return res;
      })
    );
  }

  addPresetTarget(
    id: string,
    targetId,
    form: NgForm,
    tutorialMessages,
    extraMessages,
    excludedFeatures
  ): Observable<ITarget> {
    const { position, isRequired, derivePart, deriveFromPresetTargetId } =
      form.form.value;

    let body = {};

    body = {
      targetId,
      position,
      isRequired,
      tutorialMessages,
      extraMessages,
    };

    if (derivePart !== undefined || derivePart != 'null')
      body['derivePart'] = derivePart;

    if (
      deriveFromPresetTargetId !== undefined ||
      deriveFromPresetTargetId != 'null'
    )
      body['deriveFromPresetTargetId'] = deriveFromPresetTargetId;

    if (excludedFeatures.length) {
      body['excludedFeatures'] = excludedFeatures;
    }

    return this.httpClient
      .post(apiParameters.url + '/presets/' + id + '/targets', body)
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  addTargetFiles(id: string, images: FormData): Observable<ITarget> {
    return this.httpClient
      .post(apiParameters.url + '/targets/' + id + '/files', images)
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  overrideTargetFiles(
    presetId: string,
    id: string,
    images: FormData
  ): Observable<ITarget> {
    return this.httpClient
      .post(
        apiParameters.url +
          '/presets/' +
          presetId +
          '/targets/' +
          id +
          '/files',
        images
      )
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  deletePresetTarget(presetId: string, targetId: string): Observable<any> {
    return this.httpClient
      .delete(
        apiParameters.url + '/presets/' + presetId + '/targets/' + targetId
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  deleteTargetFile(targetId: string, type: string): Observable<any> {
    return this.httpClient
      .delete(apiParameters.url + '/targets/' + targetId + '/' + type)
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  deleteTargetFileOnPreset(
    presetId,
    targetId: string,
    type: string
  ): Observable<any> {
    return this.httpClient
      .delete(
        apiParameters.url +
          '/presets/' +
          presetId +
          '/targets/' +
          targetId +
          '/' +
          type
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  // UPDATE TARGET
  updateAssignedTarget(
    presetId: string,
    targetId: string,
    body
  ): Observable<any> {
    // const {
    //   position,
    //   picturePosition,
    //   targetKey,
    //   showLibela,
    //   clearType
    // } = form.form.value;

    // let body: any

    // body = {
    //   position,
    //   picturePosition,
    //   targetKey,
    //   showLibela,
    //   clearType,
    //   tutorialMessages
    // }

    return this.httpClient
      .patch(
        apiParameters.url + '/presets/' + presetId + '/targets/' + targetId,
        body
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  // UPDATE TARGET
  updateTarget(targetId: string, body): Observable<any> {
    // const {
    //   position,
    //   picturePosition,
    //   targetKey,
    //   showLibela,
    //   clearType
    // } = form.form.value;

    // let body: any

    // body = {
    //   position,
    //   picturePosition,
    //   targetKey,
    //   showLibela,
    //   clearType,
    //   tutorialMessages
    // }
    return this.httpClient
      .patch(apiParameters.url + '/targets/' + targetId, body)
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  //Target Video Tutorial files
  getVideoTutorialFIles(targetId: string): Observable<ITarget> {
    return this.httpClient
      .get(apiParameters.url + '/targets/' + targetId + '/video-tutorial-files')
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }
  
  addVideoTutorialFIle(targetId: string, video: FormData): Observable<ITarget> {
    return this.httpClient
      .post(apiParameters.url + '/targets/' + targetId + '/video-tutorial-files', video)
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  editVideoTutorialFIle(targetId: string, videoId: string, video: FormData): Observable<ITarget> {
    return this.httpClient
      .patch(apiParameters.url + '/targets/' + targetId + '/video-tutorial-files/' + videoId, video)
      .pipe(
        map((res: ITarget) => {
          return res;
        })
      );
  }

  deleteVideoTutorialFIle(targetId: string, videoId: string): Observable<any> {
    return this.httpClient
      .delete(apiParameters.url + '/targets/' + targetId + '/video-tutorial-files/' + videoId)
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }
}
